import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Stack,
} from '@mui/material';
import { TeamMember } from '../../types';
import { teamApi } from '../../firebase/services';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '../../hooks/useLocalizedField';

const TeamContent: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField } = useLocalizedField();

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      const members = await teamApi.getAll();
      const activeMembers = members.filter(member => member.isActive);
      setTeamMembers(activeMembers);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <Typography>{t('team.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" gutterBottom>
            {t('team.title')}
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            {t('team.subtitle')}
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)'
          },
          gap: 4,
          mb: 4
        }}>
          {teamMembers.map((member) => (
            <Box key={member.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                {member.imageUrl && (
                  <CardMedia
                    component="img"
                    height={300}
                    image={member.imageUrl}
                    alt={member.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" color="primary.main" gutterBottom>
                      {getField(member, 'role', member.role)}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                      {member.specialties.map((specialty, index) => (
                        <Chip
                          key={index}
                          label={specialty}
                          size="small"
                          sx={{
                            bgcolor: 'primary.light',
                            color: 'text.primary',
                            mb: 1
                          }}
                        />
                      ))}
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                      sx={{
                        minHeight: '60px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {getField(member, 'bio', member.bio || '') || t('team.defaultBio')}
                    </Typography>
                  </Box>

                  {(member.phone || member.email) && (
                    <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      {member.phone && (
                        <Typography variant="body2" color="text.secondary">
                          📞 {member.phone}
                        </Typography>
                      )}
                      {member.email && (
                        <Typography variant="body2" color="text.secondary">
                          ✉️ {member.email}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

      </Container>
    </Box>
  );
};

export default TeamContent;
